import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '@features/auth';
import { type SalesforceAccount, type SalesforceAppConfig } from '@types';
import useSalesforceApi from '@features/Settings/hooks/useSalesforce.Api.ts';
import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore.ts';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { Card, CardContent, CardHeader } from '@core-ui/Components/ui/card.tsx';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar.tsx';
import { IconTrash, IconChevronDown } from '@tabler/icons-react';
import {
  DeleteIntegrationAccountDialog
} from '@features/Settings/components/integrations/DeleteIntegrationAccountDialog.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { logError } from '@lib/logger/logError.ts';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@core-ui/Components/ui/collapsible.tsx';

import { SalesforceMapping } from '@features/Settings/pages/integrationManagementPages/commponets/salesforceMaping.tsx';
import { useTranslation } from 'react-i18next';

export function SalesforcePage (): React.JSX.Element {
  const currentUser = getCurrentUser();
  const { t } = useTranslation();

  const [salesforceAccounts, setSalesforceAccounts] = useState<SalesforceAccount[]>([]);
  const [salesforceConfig, setSalesforceConfig] = useState<SalesforceAppConfig>();
  const { connectSalesforceLogin, getConnectedAccounts, getSalesforceConfigAsync, deleteAccountAsync } = useSalesforceApi();
  const setItems = useBreadcrumbStore((s) => s.setItems);

  async function handleSalesforceConnect (): Promise<void> {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }

    if (salesforceConfig == null) {
      throw new Error('No current user found in session store.');
    }
    await connectSalesforceLogin(
      currentUser?.userId,
      currentUser?.organizationId,
      salesforceConfig.sandbox,
    );
  }

  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/sessions/new' },
      { labelKey: 'breadcrumb.settings', href: '/workspace/settings/:activeTab' },  // This ensures the account tab is rendered by default.
      { labelKey: 'breadcrumb.app', href: '/workspace/settings/app' },
      { labelKey: 'breadcrumb.salesforce', href: '/workspace/settings/app/salesforce' },
    ]);
  }, [setItems]);

  useEffect(() => {
    void fetchAccounts();
    void fetchConfiguration();
  }, []);

  async function fetchConfiguration (): Promise<void> {
    try {
      if (currentUser == null) {
        throw new Error('No current user found in session store.');
      }

      const res = await getSalesforceConfigAsync(currentUser?.userId, currentUser?.organizationId);

      if (!res.success) {
        throw new Error(res.error?.message ?? 'Error fetching organizations');
      }

      setSalesforceConfig(res.data);

    } catch (err) {
      await logError(
        'Error fetching organizations',
        'OrganizationsTab.fetchOrganizations',
        { error: String((err as Error)?.message ?? err) },
        true,
        true,
      );
    }
  }

  async function fetchAccounts (): Promise<void> {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }

    const res = await getConnectedAccounts(currentUser.userId, currentUser.organizationId);

    if (res.success && res.data !== undefined) {
      setSalesforceAccounts(res.data);
    }
  }

  function getUserInitials (name?: string): string {
    if (typeof name === 'string' && name.trim().length > 0) {
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    }
    return 'U';
  }

  const handleDeleteAccount = async (nonce: string): Promise<void> => {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }

    await deleteAccountAsync(
      currentUser?.organizationId,
      currentUser?.userId,
      nonce
    );
  };

  return (
    <div className="grid gap-4">
      <header title="page-title" className="flex flex-wrap gap-2 items-center">
        <h1 className='text-xl sm:text-2xl md:text-3xl font-semibold'>{t('settings.integrationsPages.pageTitle', { integrationName: Integrations.Salesforce })}</h1>
        <Badge
          className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
          {t('settings.appTab.cardStatus.connected')}
        </Badge>
      </header>
      <h2 className="text-sm md:text-base">{t('settings.integrationsPages.pageSubtitle')}</h2>

      <Collapsible defaultOpen>
        <Card className="p-6 gap-1 rounded-2xl shadow-lg border border-border bg-card text-foreground">
          <CardHeader className="items-start w-full px-0 mb-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className='text-lg md:text-xl font-semibold'>{t('settings.integrationsPages.linkAccountsTitle')}</h2>
                <Badge
                  className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                  {t('settings.appTab.cardStatus.connected')}
                </Badge>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  <IconChevronDown className="size-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
            </div>
            <h3 className="text-sm">{t('settings.integrationsPages.linkAccountsSubtitle', { integrationName: Integrations.Salesforce })}</h3>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="grid pt-0 px-0">
              <section className="flex flex-col sm:flex-row w-full justify-between items-start sm:items-center gap-2 pb-2">
                <h4 className="text-sm">{t('settings.integrationsPages.linkedAccounts', {
                  number: salesforceAccounts.length,
                  plural: salesforceAccounts.length > 1 ? 's' : ''
                })}</h4>
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => {
                    void handleSalesforceConnect();
                  }}
                >
                  {t('settings.integrationsPages.addAccount')}
                </Button>
              </section>

              <main title="accounts-listing" className="grid max-h-[35vh] overflow-y-auto mb-2">
                {Array.from(salesforceAccounts).map((account, index) => {
                  return (
                    <div
                      key={`user-in-account-${index}`}
                      className="flex rounded-sm border border-border text-foreground px-2 py-2">
                      <div title="account-data-container" className="flex items-center w-full min-w-0">
                        <div className="flex gap-3 items-center w-full min-w-0">
                          <Avatar className="flex shrink-0 w-8 h-8 justify-center items-center">
                            <AvatarFallback
                              className="flex items-center justify-center h-8 w-8 text-foreground/90 text-md font-medium bg-muted">
                              {getUserInitials(account.email)}
                            </AvatarFallback>
                          </Avatar>

                          <p className="text-sm font-semibold truncate min-w-0">{account.name} ({account.email})</p>

                          <Badge
                            className="hidden sm:flex h-2 text-xs p-2 shrink-0 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                            {t('settings.appTab.cardStatus.connected')}
                          </Badge>
                        </div>
                      </div>
                      <div title="account-actions-box" className="flex gap-1 place-self-center shrink-0">
                        <DeleteIntegrationAccountDialog
                          trigger={
                            <i className="cursor-pointer">
                              <IconTrash className="w-4 h-4 m-0 text-destructive hover:text-muted-foreground"/>
                            </i>}
                          deleteCallback={() => {
                            void handleDeleteAccount(String(account.nonce));
                          }}/>
                      </div>
                    </div>
                  );
                })}
              </main>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <SalesforceMapping
        salesforceAccounts={salesforceAccounts}
      />
    </div>
  );
}